import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { SanctumAuthService } from '@freesixtyfive/auth';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { map, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    RouterModule,
    CardModule,
    FloatLabelModule,
    InputTextModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    ToastModule,
    ReactiveFormsModule,
  ],
  providers: [MessageService],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  htmlResetPasswordForm: FormGroup;
  public formSubmitted = false;

  private onDestroy = new Subject<void>();
  public isSent = false;
  private hash: string | null = null;

  public context = 'reset-password';

  #auth = inject(SanctumAuthService);
  #translate = inject(TranslateService);
  #messageService = inject(MessageService);

  constructor(
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
  ) {
    this.htmlResetPasswordForm = this.fb.group({
      password: ['', Validators.required],
      confirmPassword: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.activatedRoute.paramMap
      .pipe(
        takeUntil(this.onDestroy),
        map((params) => params.get('hash')),
      )
      .subscribe((hash) => (this.hash = hash));
    this.activatedRoute.url
      .pipe(
        map((value) => {
          const [value1] = value;
          this.context = value1.path;
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  public sendChangePasswordMail() {
    if (this.context === 'change-password') {
      this.#auth.forgotPassword<{ message: string }>({ email: '' }).subscribe({
        error: (e) => {
          this.#messageService.add({
            severity: 'error',
            summary: 'Login Failed',
            detail: this.#translate.instant(e.error.message),
          });
          this.isSent = false;
        },
        next: ({ message }: { message: string }) => {
          this.#messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: this.#translate.instant(message),
          });
          this.isSent = true;
        },
      });
    }
  }

  public submit(e: any) {
    e.preventDefault();
    this.formSubmitted = true;
    if (this.htmlResetPasswordForm.valid && this.hash) {
      const password = this.htmlResetPasswordForm.get('password')?.value;
      const confirmPassword =
        this.htmlResetPasswordForm.get('confirmPassword')?.value;

      console.log('Password:', password);
      console.log('Confirm Password:', confirmPassword);

      if (password !== confirmPassword) {
        this.#messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.#translate.instant('label.passwordDoesNotMatch'),
        });
        return;
      }

      this.#auth
        .resetPassword<{
          message: string;
        }>({ hash: this.hash, pw1: password, pw2: confirmPassword })
        .subscribe({
          error: (e) => {
            this.#messageService.add({
              severity: 'error',
              summary: 'Login Failed',
              detail: this.#translate.instant(e.error.message),
            });
            this.isSent = false;
          },
          next: ({ message }: { message: string }) => {
            this.#messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.#translate.instant(message),
            });
            this.isSent = true;
          },
        });
    } else {
      this.htmlResetPasswordForm.markAllAsTouched();
    }
  }

  isInvalid(controlName: string) {
    const control = this.htmlResetPasswordForm.get(controlName);
    return !!(control?.invalid && (control.touched || this.formSubmitted));
  }
}
